/**
===================================================
Module: frontend.src.types.visualizer.ts
Description: TypeScript type definitions for visualizer components
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

export interface VSLAMData {
  keyframes?: any[];
  landmark_points?: any[];
  dense_points?: any[];
  mapData?: Uint8Array;
}

export interface SessionData {
  keyframes: KeyframeMetadata[];
  mapUrl: string;
  parsedMapData?: VSLAMData;
}

export interface KeyframeMetadata {
  filename: string;
  keyframe_id: string;
  frame_index: number;
  timestamp: number;
  url: string;
}

export enum LoadingPhase {
  INITIAL = "initial",
  FETCHING_SESSION_DATA = "fetching_session_data",
  DOWNLOADING_MAP = "downloading_map",
  DOWNLOADING_KEYFRAMES = "downloading_keyframes",
  RENDERING_COMPONENTS = "rendering_components",
  READY = "ready",
  NAVIGATING = "navigating",
}

export interface VisualizationSettings {
  showLandmarks: boolean;
  showFrustums: boolean;
  showTrajectory: boolean;
  showDensePoints: boolean;
  sparsePointSizeUI: number;
  densePointSizeUI: number;
  frustumScaleUI: number;
}

export interface VisualizerState {
  // Core data
  sessionData: SessionData | null;
  currentKeyframeIndex: number;
  loadingPhase: LoadingPhase;
  error: string | null;

  // Settings
  visualizationSettings: VisualizationSettings;

  // Component readiness
  sceneReady: boolean;
  visualizationReady: boolean;

  // Memory management
  loadedKeyframes: Set<number>;
  downloadingKeyframes: Set<number>;
}

export interface VisualizerContextType {
  // State
  state: VisualizerState;

  // Core actions
  initializeSession: (sessionId: string) => Promise<void>;
  navigateToKeyframe: (index: number) => Promise<void>;
  refreshUrls: () => Promise<void>;

  // Settings actions
  updateVisualizationSettings: (
    settings: Partial<VisualizationSettings>,
  ) => void;

  // Component lifecycle
  setSceneReady: (ready: boolean) => void;
  setVisualizationReady: (ready: boolean) => void;

  // Camera controls
  resetCamera: () => void;
  registerResetCamera: (resetFn: () => void) => void;

  // Notification system
  registerNotifications: (notifications: {
    addError: (message: string) => void;
    addSuccess: (message: string) => void;
    addInfo: (message: string) => void;
  }) => void;

  // Error handling
  setError: (error: string | null) => void;
  addSuccess: (message: string) => void;
  addError: (message: string) => void;
  addInfo: (message: string) => void;
}

// Configuration constants
export const VISUALIZER_CONFIG = {
  // Default values (1-10 scale for UI)
  DEFAULT_SPARSE_POINT_SIZE: 2,
  DEFAULT_DENSE_POINT_SIZE: 3,
  DEFAULT_FRUSTUM_SCALE: 4,

  // Internal conversion ranges
  SPARSE_POINT_RANGE: { min: 0.1, max: 20.0 },
  DENSE_POINT_RANGE: { min: 0.1, max: 15.0 },
  FRUSTUM_SCALE_RANGE: { min: 0.05, max: 2.0 },

  // Memory and performance
  MAX_KEYFRAMES_IN_MEMORY: 300,
  KEYFRAME_CAROUSEL_VISIBLE_RANGE: 2,
  LANDMARK_FILTER_THRESHOLD: 0.3,
  MAX_RETRIES: 3,
};
